import { Component, ChangeDetectionStrategy, AfterContentInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { SectionFormDirective } from '@app/classes/section-form.class';
import { FormBuilder, AbstractControl, ValidationErrors } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import Vs from '@app/services/validators/validators.service';
import { APPEAL_DETAIL_FIELDS, TEXTAREA_FIELDS } from '../medical-appeals.models';
import { ModalService } from '@app/services/modal/modal.service';

@Component({
  selector: 'nh-medical-appeal-details',
  templateUrl: './medical-appeal-details.component.html',
  styleUrls: ['./medical-appeal-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MedicalAppealDetailsComponent extends SectionFormDirective implements AfterContentInit, AfterViewInit {
  showEligibilityMessage = false;
  translatedLang: string;
  inHomeCareOptions: any[] = [];

  constructor(
    public builder: FormBuilder,
    private titleService: Title,
    public translateService: TranslateService,
    private modalService: ModalService,
    private cdr: ChangeDetectorRef
  ) {
    super(builder);
    this.titleService.setTitle('Medical Appeal Details');
  }

  ngAfterContentInit() {
    // Disable all conditional fields by default
    this.formGroup.controls.data.disable();
    // Enable always-visible trigger questions
    this.enableAlwaysVisibleFields();
    // Check existing values and enable appropriate sections
    this.initializeConditionalSections();
    // Add custom validator to disable NEXT button when eligibility message is shown
    this.formGroup.setValidators(this.eligibilityValidator.bind(this));
    // Initialize language handling
    this.translatedLang = this.translateService.getDefaultLang();
    this.inHomeCareOptions = this.tableCodeValue('KINDOFINHOMECARE', 'data.kindOfInHomeCareCd') || [];
    this.translateService.onDefaultLangChange.subscribe((res) => {
      this.translatedLang = res.lang;
      this.inHomeCareOptions = this.tableCodeValue('KINDOFINHOMECARE', 'data.kindOfInHomeCareCd') || [];
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const heading = document.querySelector('h1.page-title-h1') as HTMLElement | null;
      if (heading) {
        heading.style.outline = 'none';
        heading.focus();
      }
    }, 100);
  }

  /**
   * Custom validator that makes form invalid when eligibility message is shown
   * This prevents the NEXT button from being enabled
   */
  private eligibilityValidator(_control: AbstractControl): ValidationErrors | null {
    if (this.showEligibilityMessage) {
      return { 'ineligibleAppeal': true };
    }
    return null;
  }

  private enableAlwaysVisibleFields() {
    // Enable read-only fields that must be included in payload
    this.getControl('data.appNum').enable();
    const appealNum = this.getValue('data.appNum');
    sessionStorage.setItem('appealNum', appealNum);
    // Enable paid out of pocket question (user-editable)
    this.getControl('data.paidOutOfPocketSw').enable();
  }

  private initializeConditionalSections() {
    // Enable all controls based on current values (for edit mode)
    // Enable out of pocket details if YES
    if (this.isY('data.paidOutOfPocketSw')) {
      this.enableControlWithValidator('data.paidServiceTypeCd');
      this.enableControlWithValidator('data.paidServiceDtlsTxt');
    }
    // Enable existing bills question if paid out of pocket = NO
    if (this.isN('data.paidOutOfPocketSw')) {
      this.enableControlWithValidator('data.hasExistingBillsSw');
    }
    // Enable billing details if existing bills = YES
    if (this.isY('data.hasExistingBillsSw')) {
      this.enableControlWithValidator('data.billedServiceTypeCd');
      this.enableControlWithValidator('data.billedServiceDtlsTxt');
    }
    // Enable denied care question if existing bills = NO
    if (this.isN('data.hasExistingBillsSw')) {
      this.enableControlWithValidator('data.deniedRqstdCareSw');
      // Enable already received if denied care = YES
      if (this.isY('data.deniedRqstdCareSw')) {
        this.enableControlWithValidator('data.alrdyRcvdDeniedCareSw');
        // Enable denied service details if already received = NO
        if (this.isN('data.alrdyRcvdDeniedCareSw')) {
          this.enableControlWithValidator('data.deniedServiceTypeCd');
          const serviceTypeCode = this.getValue('data.deniedServiceTypeCd')?.code;
          // Enable pharmacy lock-in if Medicine selected
          if (serviceTypeCode === 'PH') { // PH = Medicine
            this.enableControlWithValidator('data.medDeniedAssignedPharmacySw');
            // Enable pharmacy request type if lock-in = YES
            if (this.isY('data.medDeniedAssignedPharmacySw')) {
              this.enableControlWithValidator('data.pharmacyRequestTypeCd');
              // Enable change/remove fields based on request type
              const requestType = this.getValue('data.pharmacyRequestTypeCd')?.code;
              if (requestType === 'CDP') { // CDP = Change to different pharmacy
                this.enableControlWithValidator('data.currentPharmacyChngRsnTxt');
                this.enableControlWithValidator('data.rqstdPharmacyTxt');
                this.enableControlWithValidator('data.pharmacyChngRsnTxt');
              } else if (requestType === 'RPL') { // RPL = Remove Pharmacy lock-in
                this.enableControlWithValidator('data.currentPharmacyRmvLockinTxt');
                this.enableControlWithValidator('data.rmvLockinRsnTxt');
              }
            }
            // Enable additional details if lock-in = NO
            if (this.isN('data.medDeniedAssignedPharmacySw')) {
              this.enableControlWithValidator('data.aplDtlsNoLockinRsnTxt');
            }
          } else if (serviceTypeCode && serviceTypeCode !== 'PH') {
            // Enable additional details for Medical care (MC) or Dental care (DE)
            this.enableControlWithValidator('data.aplDtlsMedDentalTxt');
          }
        }
        // If already received = YES, go to in-home care
        if (this.isY('data.alrdyRcvdDeniedCareSw')) {
          this.enableControlWithValidator('data.missedShiftCareSw');
        }
      }
      // If denied care = NO, go to in-home care
      if (this.isN('data.deniedRqstdCareSw')) {
        this.enableControlWithValidator('data.missedShiftCareSw');
      }
    }
    // Enable in-home care details if in-home care = YES
    if (this.isY('data.missedShiftCareSw')) {
      this.enableControlWithValidator('data.kindOfInHomeCareCd');
      // Enable "Other" text field if "Other" is selected
      const selections = this.getValue('data.kindOfInHomeCareCd') || [];
      const hasOther = selections.some((item: any) => item?.code === 'OT');
      if (hasOther) {
        this.enableControlWithValidator('data.inHomeCareTypeOthTxt');
      }
    }
    // Enable assigned pharmacy question if in-home care = NO
    if (this.isN('data.missedShiftCareSw')) {
      this.enableControlWithValidator('data.assignedPharmacyRequiredSw');
    }
    // Handle assigned pharmacy logic
    if (this.isY('data.assignedPharmacyRequiredSw')) {
      // Loop back to pharmacy section
      this.enableControlWithValidator('data.pharmacyRequestTypeCd');
      const requestType = this.getValue('data.pharmacyRequestTypeCd')?.code;
      if (requestType === 'CDP') { // CDP = Change to different pharmacy
        this.enableControlWithValidator('data.currentPharmacyChngRsnTxt');
        this.enableControlWithValidator('data.rqstdPharmacyTxt');
        this.enableControlWithValidator('data.pharmacyChngRsnTxt');
      } else if (requestType === 'RPL') { // RPL = Remove Pharmacy lock-in
        this.enableControlWithValidator('data.currentPharmacyRmvLockinTxt');
        this.enableControlWithValidator('data.rmvLockinRsnTxt');
      }
    } else if (this.isN('data.assignedPharmacyRequiredSw')) {
      this.showEligibilityMessage = true;
      this.modalService.openAppealEligibility();
    }
  }

  // Simplified change handlers - just enable/disable controls
  onChange(controlName: string) {
    const value = this.getValue(`data.${controlName}`);

    switch (controlName) {
      case 'paidOutOfPocketSw':
        this.handlePaidOutOfPocketChange(value?.code);
        break;

      case 'paidServiceTypeCd':
        this.getControl('data.paidServiceDtlsTxt').setValue('');
        break;

      case 'hasExistingBillsSw':
        this.handleExistingBillsChange(value?.code);
        break;

      case 'billedServiceTypeCd':
        this.getControl('data.billedServiceDtlsTxt').setValue('');
        break;

      case 'deniedRqstdCareSw':
        this.handleDeniedCareChange(value?.code);
        break;

      case 'alrdyRcvdDeniedCareSw':
        this.handleAlreadyReceivedChange(value?.code);
        break;

      case 'deniedServiceTypeCd':
        this.handleDeniedServiceTypeChange(value?.code);
        break;

      case 'medDeniedAssignedPharmacySw':
        this.handlePharmacyLockInChange(value?.code);
        break;

      case 'pharmacyRequestTypeCd':
        this.handlePharmacyRequestTypeChange(value?.code);
        break;

      case 'missedShiftCareSw':
        this.handleInHomeCareChange(value?.code);
        break;

      case 'assignedPharmacyRequiredSw':
        this.handleAssignedPharmacyChange(value?.code);
        break;
    }
  }

  // Simplified change handlers - just enable/disable controls
  private handlePaidOutOfPocketChange(code: string) {
    if (code === 'Y') {
      // YES: Enable out of pocket details (service type + details)
      this.enableControlsWithValidators(APPEAL_DETAIL_FIELDS.OUT_OF_POCKET);
      // Reset entire existing bills branch (downstream cascade)
      this.resetAndDisableControl('data.hasExistingBillsSw');
      this.resetAndDisableControl('data.billedServiceTypeCd');
      this.resetAndDisableControl('data.billedServiceDtlsTxt');
      this.resetAndDisableControl('data.deniedRqstdCareSw');
      this.resetEntireDeniedCareBranch();
      this.resetEntireInHomeCareBranch();
    } else if (code === 'N') {
      // NO: Enable existing bills question
      this.enableControlWithValidator('data.hasExistingBillsSw');
      // Reset entire out of pocket details branch
      this.resetAndDisableControl('data.paidServiceTypeCd');
      this.resetAndDisableControl('data.paidServiceDtlsTxt');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handleExistingBillsChange(code: string) {
    if (code === 'Y') {
      // Enable billing details
      this.enableControlsWithValidators(APPEAL_DETAIL_FIELDS.BILLING);
      // Reset entire denied care branch and in-home care branch (downstream cascade)
      this.resetAndDisableControl('data.deniedRqstdCareSw');
      this.resetEntireDeniedCareBranch();
      this.resetEntireInHomeCareBranch();
    } else if (code === 'N') {
      // Reset billing details
      this.resetAndDisableControl('data.billedServiceTypeCd');
      this.resetAndDisableControl('data.billedServiceDtlsTxt');
      // Enable denied care question
      this.enableControlWithValidator('data.deniedRqstdCareSw');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handleDeniedCareChange(code: string) {
    if (code === 'Y') {
      // Enable already received question
      this.enableControlWithValidator('data.alrdyRcvdDeniedCareSw');
      // Reset in-home care branch
      this.resetEntireInHomeCareBranch();
    } else if (code === 'N') {
      // Reset entire already received branch (downstream cascade)
      this.resetAndDisableControl('data.alrdyRcvdDeniedCareSw');
      this.resetAndDisableControl('data.deniedServiceTypeCd');
      this.resetAndDisableControl('data.aplDtlsMedDentalTxt');
      this.resetAndDisableControl('data.medDeniedAssignedPharmacySw');
      this.resetEntirePharmacyBranch();
      // Enable in-home care question (denied care NO path goes to Q-A21)
      this.enableControlWithValidator('data.missedShiftCareSw');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handleAlreadyReceivedChange(code: string) {
    if (code === 'Y') {
      // Reset entire denied service + pharmacy branch
      this.resetAndDisableControl('data.deniedServiceTypeCd');
      this.resetAndDisableControl('data.aplDtlsMedDentalTxt');
      this.resetAndDisableControl('data.medDeniedAssignedPharmacySw');
      this.resetEntirePharmacyBranch();
      // Enable in-home care question (already received YES path goes to Q-A21)
      this.enableControlWithValidator('data.missedShiftCareSw');
    } else if (code === 'N') {
      // Reset in-home care branch
      this.resetEntireInHomeCareBranch();
      // Enable denied service type (details enabled by handleDeniedServiceTypeChange)
      this.enableControlWithValidator('data.deniedServiceTypeCd');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handleDeniedServiceTypeChange(value: string) {
    if (value === 'PH') { // PH = Medicine
      // Reset in-home care branch and additional details
      this.resetEntireInHomeCareBranch();
      this.resetAndDisableControl('data.aplDtlsMedDentalTxt');
      // Enable pharmacy lock-in question
      this.enableControlWithValidator('data.medDeniedAssignedPharmacySw');
    } else {
      // Medical care (MC) or Dental care (DE)
      // Reset entire pharmacy branch
      this.resetAndDisableControl('data.medDeniedAssignedPharmacySw');
      this.resetEntirePharmacyBranch();
      // Reset textarea in case user switched between two non-PH values (e.g. MC → DE)
      this.getControl('data.aplDtlsMedDentalTxt').setValue('');
      // Enable additional details for Medical/Dental care
      this.enableControlWithValidator('data.aplDtlsMedDentalTxt');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handlePharmacyLockInChange(code: string) {
    if (code === 'Y') {
      // Enable pharmacy request type
      this.enableControlWithValidator('data.pharmacyRequestTypeCd');
      // Reset additional details
      this.resetAndDisableControl('data.aplDtlsNoLockinRsnTxt');
    } else if (code === 'N') {
      // Reset all pharmacy request fields
      this.resetAndDisableControl('data.pharmacyRequestTypeCd');
      this.resetAndDisableControl('data.currentPharmacyChngRsnTxt');
      this.resetAndDisableControl('data.rqstdPharmacyTxt');
      this.resetAndDisableControl('data.pharmacyChngRsnTxt');
      this.resetAndDisableControl('data.currentPharmacyRmvLockinTxt');
      this.resetAndDisableControl('data.rmvLockinRsnTxt');
      // Enable additional details
      this.enableControlWithValidator('data.aplDtlsNoLockinRsnTxt');
    }
    this.formGroup.updateValueAndValidity();
  }

  private handlePharmacyRequestTypeChange(code: string) {
    // Disable both sets first
    this.resetAndDisableControl('data.currentPharmacyChngRsnTxt');
    this.resetAndDisableControl('data.rqstdPharmacyTxt');
    this.resetAndDisableControl('data.pharmacyChngRsnTxt');
    this.resetAndDisableControl('data.currentPharmacyRmvLockinTxt');
    this.resetAndDisableControl('data.rmvLockinRsnTxt');

    // Enable appropriate set
    if (code === 'CDP') { // CDP = Change to different pharmacy
      this.enableControlsWithValidators(APPEAL_DETAIL_FIELDS.PHARMACY_CHANGE);
    } else if (code === 'RPL') { // RPL = Remove Pharmacy lock-in
      this.enableControlsWithValidators(APPEAL_DETAIL_FIELDS.PHARMACY_REMOVE);
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  private handleInHomeCareChange(code: string) {
    if (code === 'Y') {
      // YES: Enable in-home care details, reset assigned pharmacy branch
      this.showEligibilityMessage = false;
      this.enableControlWithValidator('data.kindOfInHomeCareCd');
      // Note: inHomeCareTypeOthTxt is enabled by handleKindOfInHomeCareChange when "Other" is selected
      // Reset assigned pharmacy + pharmacy branch (downstream cascade)
      this.resetAndDisableControl('data.assignedPharmacyRequiredSw');
      this.resetEntirePharmacyBranch();
    } else if (code === 'N') {
      // NO: Reset in-home care details, enable assigned pharmacy question
      this.resetAndDisableControl('data.kindOfInHomeCareCd');
      this.resetAndDisableControl('data.inHomeCareTypeOthTxt');
      this.enableControlWithValidator('data.assignedPharmacyRequiredSw');
    }
    // Trigger parent form validation
    this.formGroup.updateValueAndValidity();
  }

  onInHomeCareCheckboxChange(option: any, event: any): void {
    const control = this.getControl('data.kindOfInHomeCareCd');
    const currentValue: any[] = control.value || [];
    const updated = event?.target?.checked ? [...currentValue, option] : currentValue.filter((item: any) => item.code !== option.code);
    control.setValue(updated);
    control.markAsTouched();
    const hasOther = updated.some((item: any) => item?.code === 'OT');
    const otherControl = this.getControl('data.inHomeCareTypeOthTxt');
    if (hasOther && otherControl.disabled) {
      this.enableControlWithValidator('data.inHomeCareTypeOthTxt');
    } else if (!hasOther && otherControl.enabled) {
      this.resetAndDisableControl('data.inHomeCareTypeOthTxt');
    }
    this.cdr.detectChanges();
  }

  isInHomeCareSelected(optionCode: string): boolean {
    return (this.getValue('data.kindOfInHomeCareCd') || []).some((item: any) => item?.code === optionCode);
  }

  private handleAssignedPharmacyChange(code: string) {
    if (code === 'Y') {
      this.enableControlWithValidator('data.pharmacyRequestTypeCd');
      this.showEligibilityMessage = false;
    } else if (code === 'N') {
      // Reset entire pharmacy section
      this.resetEntirePharmacyBranch();
      // Set message (validator will make form invalid) and show modal
      this.showEligibilityMessage = true;
      this.modalService.openAppealEligibility();
    } else {
      this.showEligibilityMessage = false;
    }
    // Trigger parent form validation (will run eligibilityValidator)
    this.formGroup.updateValueAndValidity();
  }

  // Utility method to reset, clear validators, and disable a control
  private resetAndDisableControl(path: string) {
    const control = this.getControl(path);
    control.reset();
    control.clearValidators();
    control.updateValueAndValidity();
    control.disable();
  }

  // Helper methods to reset entire branches (prevents orphaned validators)
  private resetEntirePharmacyBranch() {
    this.resetAndDisableControl('data.pharmacyRequestTypeCd');
    this.resetAndDisableControl('data.aplDtlsNoLockinRsnTxt');
    this.resetAndDisableControl('data.currentPharmacyChngRsnTxt');
    this.resetAndDisableControl('data.rqstdPharmacyTxt');
    this.resetAndDisableControl('data.pharmacyChngRsnTxt');
    this.resetAndDisableControl('data.currentPharmacyRmvLockinTxt');
    this.resetAndDisableControl('data.rmvLockinRsnTxt');
  }

  private resetEntireInHomeCareBranch() {
    this.showEligibilityMessage = false;
    this.resetAndDisableControl('data.missedShiftCareSw');
    this.resetAndDisableControl('data.kindOfInHomeCareCd');
    this.resetAndDisableControl('data.inHomeCareTypeOthTxt');
    this.resetAndDisableControl('data.assignedPharmacyRequiredSw');
    this.resetEntirePharmacyBranch();
  }

  private resetEntireDeniedCareBranch() {
    this.resetAndDisableControl('data.alrdyRcvdDeniedCareSw');
    this.resetAndDisableControl('data.deniedServiceTypeCd');
    this.resetAndDisableControl('data.aplDtlsMedDentalTxt');
    this.resetAndDisableControl('data.medDeniedAssignedPharmacySw');
    this.resetEntirePharmacyBranch();
  }

  /**
   * Enable a single control and set required validator
   * For textarea fields, also adds textarea character validation
   * @param path - Control path (e.g., 'data.fieldName')
   */
  private enableControlWithValidator(path: string): void {
    const control = this.getControl(path);
    control.enable();
    // Textarea fields need both required and textarea validators
    if (TEXTAREA_FIELDS.includes(path as any)) {
      control.setValidators([Vs.required, Vs.textarea]);
    } 
    // Array fields need custom validator (at least one selected)
    else if (path === 'data.kindOfInHomeCareCd') {
      control.setValidators((ctrl: AbstractControl) => {
        const arr = ctrl.value || [];
        return arr.length > 0 ? null : { required: true };
      });
    }
    else {
      control.setValidators(Vs.required);
    }
    control.updateValueAndValidity();
  }

  /**
   * Enable multiple controls and set required validators
   * @param paths - Array of control paths
   */
  private enableControlsWithValidators(paths: readonly string[]): void {
    paths.forEach(path => this.enableControlWithValidator(path));
  }

  // Table code value helper method for template
  tableCodeValue(tableName: string, tableControl: string) {
    if (tableName === 'KINDOFINHOMECARE') {
      return (this.tables[tableName] || []).map((element: any) => ({
        code: element.code,
        value: this.translatedLang === 'sp' ? element.spDescription : element.description,
      }));
    }
    return this.getTableCodeValues(tableName, tableControl, this.translatedLang, false);
  }

  // Filter emojis from textarea input
  onInput(event: Event) {
    const input = event.target as HTMLTextAreaElement;
    const cursorPosition = input.selectionStart;
    const value = input.value;
    const filteredValue = this.removeEmojis(value);
    // Update the textarea value without emojis
    input.value = filteredValue;
    // Restore the cursor position
    input.setSelectionRange(cursorPosition, cursorPosition);
    const control = this.getControl(`data.${input.name}`, this.formGroup);
    control.patchValue(filteredValue);
    control.updateValueAndValidity();
    control.markAsTouched();
    this.cdr.detectChanges();
  }

  removeEmojis(value: string) {
    const emojiRegex = /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF]|[\r])/g;
    return value.replace(emojiRegex, '');
  }
}